/**
 * Menu da sala: o DropdownMenu do Radix com a cara da Touch (cantos grandes, fundo escuro da sala,
 * item ativo com check ciano) e o portal dentro do elemento da sala, para aparecer em tela cheia.
 * O `ui/dropdown-menu` da plataforma abre sempre em `document.body`, por isso a sala tem o seu.
 */
import { forwardRef, useCallback, useEffect, useState } from 'react';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRoomPortalContainer } from './roomPortal';

export const RoomMenu = Menu.Root;
export const RoomMenuTrigger = Menu.Trigger;
export const RoomMenuRadioGroup = Menu.RadioGroup;

const itemBase =
    'flex min-h-10 cursor-default select-none items-center gap-3 rounded-lg px-3 py-1.5 text-sm outline-none transition-colors focus:bg-white/10 data-[disabled]:pointer-events-none data-[disabled]:opacity-50';

/**
 * O Radix reposiciona o menu quando o conteúdo muda de tamanho, com o ResizeObserver DA GUIA. Na janela
 * flutuante (outro documento) a guia costuma estar em segundo plano, e o navegador deixa de entregar a
 * ela os avisos de tamanho dos elementos da outra janela: o menu abre pequeno, cresce para baixo e cobre o
 * botão que o abriu. Aqui o conteúdo é observado pelo ResizeObserver da janela onde ele está e o
 * reposicionamento é pedido pelo evento `resize` dessa janela, que o Radix também escuta.
 */
export function useRepositionInOwnWindow(node) {
    useEffect(() => {
        const win = node?.ownerDocument?.defaultView;
        if (!node || !win || win === window || typeof win.ResizeObserver !== 'function') return undefined;
        const observer = new win.ResizeObserver(() => win.dispatchEvent(new win.Event('resize')));
        observer.observe(node);
        return () => observer.disconnect();
    }, [node]);
}

export const RoomMenuContent = forwardRef(function RoomMenuContent({ className, sideOffset = 10, ...props }, ref) {
    const container = useRoomPortalContainer();
    const [node, setNode] = useState(null);
    useRepositionInOwnWindow(node);
    const setRefs = useCallback((el) => {
        setNode(el);
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
    }, [ref]);
    return (
        <Menu.Portal container={container}>
            <Menu.Content
                ref={setRefs}
                side="top"
                sideOffset={sideOffset}
                collisionPadding={8}
                className={cn(
                    'z-50 min-w-[260px] max-w-[min(340px,calc(100vw-16px))] rounded-2xl border border-white/10 bg-neutral-900 p-1.5 text-neutral-50 shadow-2xl outline-none',
                    // O menu da câmera (dispositivos + fundo) é alto: numa janela pequena rola em vez de sair dela.
                    'max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto',
                    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                    className,
                )}
                {...props}
            />
        </Menu.Portal>
    );
});

export const RoomMenuItem = forwardRef(function RoomMenuItem({ className, icon: Icon, description, children, ...props }, ref) {
    return (
        <Menu.Item ref={ref} className={cn(itemBase, className)} {...props}>
            {Icon && <Icon className="h-[18px] w-[18px] shrink-0 text-neutral-300" aria-hidden="true" />}
            <span className="min-w-0 flex-1">
                <span className="block truncate">{children}</span>
                {description && <span className="block truncate text-xs text-neutral-400">{description}</span>}
            </span>
        </Menu.Item>
    );
});

/** Opção única de uma lista (dispositivo): o ativo mostra um check. */
export const RoomMenuRadioItem = forwardRef(function RoomMenuRadioItem({ className, children, ...props }, ref) {
    return (
        <Menu.RadioItem ref={ref} className={cn(itemBase, 'data-[state=checked]:bg-white/5', className)} {...props}>
            <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">
                <Menu.ItemIndicator>
                    <Check className="h-4 w-4 text-cyan-300" aria-hidden="true" />
                </Menu.ItemIndicator>
            </span>
            <span className="min-w-0 flex-1 truncate">{children}</span>
        </Menu.RadioItem>
    );
});

export const RoomMenuLabel = forwardRef(function RoomMenuLabel({ className, ...props }, ref) {
    return (
        <Menu.Label
            ref={ref}
            className={cn('px-3 pb-1 pt-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400', className)}
            {...props}
        />
    );
});

export const RoomMenuSeparator = forwardRef(function RoomMenuSeparator({ className, ...props }, ref) {
    return <Menu.Separator ref={ref} className={cn('my-1.5 h-px bg-white/10', className)} {...props} />;
});
